import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import ReflectionRichText from "./ReflectionRichText.js";
import BehaviorPatternInsight from "./BehaviorPatternInsight.js";
import EmotionInsightDisplay from "./EmotionInsightDisplay.js";
import QuickClarityResult from "./QuickClarityResult.js";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";
import { startCheckout } from "../utils/startCheckout.js";
import { applyAccessFromApi } from "../utils/applyAccessFromApi.js";
import {
  PRICING_MEMBER_PRICE_SUMMARY,
  FREE_AI_INSIGHTS_TOTAL,
  FREE_MAX_WORDS_PER_ENTRY
} from "../constants/pricing.js";
import { getFriendlyApiError } from "../utils/netlifyFunctionError.js";
import { IconThumbDown, IconThumbUp, IconTrash } from "./RatingIcons.js";
import { isQuickClarityInsight } from "../utils/isQuickClarityInsight.js";
import thinkingPersonImg from "../assets/illustrations/quick-clarity/thinking-person.svg";
import { useSpeechDictation } from "../hooks/useSpeechDictation.js";
import {
  clearPendingQuickClarity,
  getPendingQuickClarity,
  setPendingQuickClarity,
  QUICK_CLARITY_LOGIN_INTENT
} from "../utils/pendingQuickClarity.js";
import { getStoredUserId, clearHistoryCacheForUser } from "../utils/userSession.js";

function validateQuickClarityEntry(trimmed) {
  if (!trimmed) {
    return "Please enter your gratitude note first.";
  }
  const testLikeWords = ["text", "test", "asdf", "abc", "hello", "hi", "ok", "no", "yes", "qwerty", "sample", "demo", "xxx"];
  const words = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
  const isOnlyTestLike = words.length <= 3 && words.every((w) => testLikeWords.includes(w.replace(/[^a-z]/g, "")));
  const tooShort = words.length < 5;
  if (isOnlyTestLike || tooShort) {
    return "Please add valid content.";
  }
  const wordCount = trimmed.trim().split(/\s+/).filter(Boolean).length;
  if (wordCount > FREE_MAX_WORDS_PER_ENTRY) {
    return `Please keep your entry within ${FREE_MAX_WORDS_PER_ENTRY} words.`;
  }
  return null;
}

const BrainDumpGuideCollapse = ({ summary = "What is Quick Clarity?", className = "" }) => (
  <details className={`home-framework-guide__details intro-card__brain-dump-read brain-dump__guide${className ? ` ${className}` : ""}`}>
    <summary className="home-framework-guide__summary">{summary}</summary>
    <div className="home-framework-guide__details-body intro-card__brain-dump-body">
      <h3 className="intro-card__brain-section-title">What is Quick Clarity?</h3>
      <p>
        Record the thought, situation, conversation, or decision occupying your attention. The goal is to examine
        interpretations against evidence and leave with one clear, testable insight—not to complete a full thought record.
      </p>
      <h3 className="intro-card__brain-section-title">Why write it down?</h3>
      <p>Unexamined loops raise mental load and blur fact from inference. A short written entry helps you:</p>
      <ul className="intro-card__brain-list">
        <li>
          <strong>Separate fact from interpretation:</strong> Name what happened versus the meaning you assigned.
        </li>
        <li>
          <strong>Reduce open loops:</strong> Capture unfinished thoughts so they stop competing for attention.
        </li>
        <li>
          <strong>Choose a response:</strong> Decide what to do next based on evidence, not the first story that appears.
        </li>
      </ul>
    </div>
  </details>
);

const MicIcon = () => (
  <svg className="qc-dictate__icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none">
    <path
      d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M5 11a7 7 0 0 0 14 0M12 18v3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const QUICK_CLARITY_CUES = [
  "Today I had a … day",
  "I wonder …",
  "I don’t know why …",
  "I never wanted to …",
  "I keep thinking about …",
  "Something I can’t stop replaying is …",
  "I wish I had …",
  "What I haven’t said yet is …",
  "I’m stuck on …",
  "After what happened, I …"
];

const ReflectionInputCard = ({ value, onChange, maxChars = 300 }) => {
  const [showCues, setShowCues] = useState(false);
  const cuesCloseRef = useRef(null);
  const { supported, listening, error, toggle, clearError, setUserText } = useSpeechDictation({
    value,
    onChange,
    maxChars
  });

  useEffect(() => {
    if (!showCues) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setShowCues(false);
    };
    window.addEventListener("keydown", onKey);
    cuesCloseRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [showCues]);

  const openCues = () => setShowCues(true);

  const insertCue = (cue) => {
    clearError();
    const next = value.trim() ? `${value.trim()}\n\n${cue}` : cue;
    setUserText(next.slice(0, maxChars));
    setShowCues(false);
  };

  return (
    <div className="reflection-input-card">
      <div className="reflection-input-card__art reflection-input-card__art--left">
        <img
          src={thinkingPersonImg}
          alt=""
          className="reflection-input-card__img"
          width={160}
          height={200}
          decoding="async"
          aria-hidden="true"
        />
      </div>
      <div className="reflection-input-card__editor">
        <textarea
          id="gratitude-entry"
          className="personalized-textarea reflection-textarea"
          value={value}
          onChange={(event) => {
            clearError();
            setUserText(event.target.value);
          }}
          rows={12}
          maxLength={maxChars}
          placeholder={"What is taking up space in your mind right now?\nWrite it as it is—unfinished, emotional or uncertain. You don’t need to organize it first."}
        />
        <div className="reflection-input-card__footer">
          <div className="char-counter">
            {value.length}/{maxChars}
          </div>
          <div className="reflection-input-card__actions">
            <button
              type="button"
              className="qc-cues-btn"
              onClick={openCues}
              aria-haspopup="dialog"
              aria-expanded={showCues}
            >
              Show me some cues
            </button>
            {supported ? (
              <button
                type="button"
                className={`qc-dictate${listening ? " qc-dictate--listening" : ""}`}
                onClick={toggle}
                aria-pressed={listening}
                aria-label={listening ? "Stop dictation" : "Dictate into the entry"}
              >
                <MicIcon />
                <span>{listening ? "Stop" : "Dictate"}</span>
              </button>
            ) : null}
          </div>
        </div>
        {listening ? (
          <p className="qc-dictate__status" role="status" aria-live="polite">
            Listening… Speech may be processed by your browser vendor. Ahavibes stores the text in this box, not audio.
          </p>
        ) : null}
        {error ? (
          <p className="qc-dictate__error" role="status">
            {error}
          </p>
        ) : null}
      </div>

      {showCues ? (
        <div
          className="qc-cues-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setShowCues(false);
          }}
        >
          <div
            className="qc-cues-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="qc-cues-title"
          >
            <div className="qc-cues-dialog__header">
              <h3 id="qc-cues-title">Writing cues</h3>
              <button
                ref={cuesCloseRef}
                type="button"
                className="qc-cues-dialog__close"
                onClick={() => setShowCues(false)}
                aria-label="Close cues"
              >
                ×
              </button>
            </div>
            <p className="qc-cues-dialog__intro">
              Pick a starter that fits. Finish it in your own words—unfinished is fine.
            </p>
            <ul className="qc-cues-list">
              {QUICK_CLARITY_CUES.map((cue) => (
                <li key={cue}>
                  <button type="button" className="qc-cues-list__btn" onClick={() => insertCue(cue)}>
                    {cue}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const InsightSummary = ({ insight }) => {
  const n = insight || {};
  const isClarityShape = [n.event_reflection, n.interpretation_reflection, n.evidence_reflection, n.response_statement].some(
    Boolean
  );

  const emotionBlock = (
    <>
      <h3>Emotion</h3>
      <EmotionInsightDisplay sentiment={n.sentiment} emotionCategory={n.emotion_category} />
    </>
  );

  if (isClarityShape) {
    return (
      <div className="insight-summary__body">
        {emotionBlock}

        {n.behavior_pattern ? <BehaviorPatternInsight pattern={n.behavior_pattern} action={n.behavior_action} /> : null}

        {n.event_reflection ? (
          <>
            <h3>What happened with you (observable facts)</h3>
            <div className="insight-summary__block insight-summary__rich">
              <ReflectionRichText text={n.event_reflection} />
            </div>
          </>
        ) : null}

        {n.interpretation_reflection ? (
          <>
            <h3>Interpretation</h3>
            <div className="insight-summary__block insight-summary__rich">
              <ReflectionRichText text={n.interpretation_reflection} />
            </div>
          </>
        ) : null}

        {n.evidence_reflection ? (
          <>
            <h3>Evidence</h3>
            <div className="insight-summary__block insight-summary__rich">
              <ReflectionRichText text={n.evidence_reflection} />
            </div>
          </>
        ) : null}

        {n.response_statement ? (
          <>
            <h3>Response</h3>
            <div className="insight-summary__block insight-summary__rich">
              <ReflectionRichText text={n.response_statement} />
            </div>
          </>
        ) : null}
      </div>
    );
  }

  return <div className="insight-summary__body">{emotionBlock}</div>;
};

const BrainDump = ({
  verifiedEmail,
  personalEntries,
  setPersonalEntries,
  isSubscriber,
  setIsSubscriber,
  setIsPaidSubscriber,
  isVerified,
  embedded = false
}) => {
  const navigate = useNavigate();
  const [entry, setEntry] = useState("");
  const [insight, setInsight] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isPaywalled, setIsPaywalled] = useState(false);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [hasFetchedInsights, setHasFetchedInsights] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const resultsRef = useRef(null);
  const resumePendingRef = useRef(false);

  useEffect(() => {
    if (isSubscriber) {
      setIsPaywalled(false);
    } else {
      setIsPaywalled(personalEntries.length >= FREE_AI_INSIGHTS_TOTAL);
    }
  }, [personalEntries.length, isSubscriber]);

  const freeInsightsUsed = personalEntries.length;

  const loadInsights = useCallback(async () => {
    if (!verifiedEmail) return;
    setIsLoadingInsights(true);
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getUserInsights`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifiedEmail })
      });
      if (!response.ok) {
        const errText = await response.text();
        let apiError = "Unable to load insights.";
        try {
          const parsed = JSON.parse(errText);
          if (parsed && typeof parsed.error === "string") apiError = parsed.error;
        } catch (_) {}
        throw new Error(apiError);
      }
      const data = await response.json();
      setPersonalEntries(Array.isArray(data.insights) ? data.insights : []);
      applyAccessFromApi(data, setIsSubscriber, setIsPaidSubscriber);
      setHasFetchedInsights(true);
      setError("");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to load your insights right now.");
    } finally {
      setIsLoadingInsights(false);
    }
  }, [verifiedEmail, setPersonalEntries, setIsSubscriber, setIsPaidSubscriber]);

  useEffect(() => {
    setHasFetchedInsights(false);
    setInsight(null);
    setError("");
    // Keep draft after login — do not wipe pending Quick Clarity text.
    const pending = getPendingQuickClarity().trim();
    setEntry(pending || "");
  }, [verifiedEmail]);

  useEffect(() => {
    if (verifiedEmail && !hasFetchedInsights) {
      loadInsights();
    }
  }, [verifiedEmail, hasFetchedInsights, loadInsights]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      setHasFetchedInsights(false);
      params.delete("checkout");
      if (!params.get("tab")) {
        params.set("tab", "brain-dump");
      }
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}`;
      window.history.replaceState({}, "", nextUrl);
      const emailForSync = verifiedEmail || localStorage.getItem("ahavibes_verified_email") || "";
      if (emailForSync) {
        fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getUserInsights`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: emailForSync })
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data) applyAccessFromApi(data, setIsSubscriber, setIsPaidSubscriber);
          })
          .catch((err) => console.error("Unable to sync subscriber status after checkout.", err));
      }
    }
  }, [verifiedEmail, setIsSubscriber, setIsPaidSubscriber]);

  const submitQuickClarity = useCallback(
    async (trimmed) => {
      if (!verifiedEmail) return false;
      if (!isSubscriber && freeInsightsUsed >= FREE_AI_INSIGHTS_TOTAL) {
        setError("Free insight limit reached. Subscribe to continue reflecting with AI.");
        return false;
      }
      if (!isSubscriber && isPaywalled) {
        setError("Please subscribe to unlock more insights.");
        return false;
      }
      setError("");
      setIsSubmitting(true);
      try {
        const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getPersonalizedInsights`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entry: trimmed, email: verifiedEmail, mode: "quick_clarity" })
        });
        const text = await response.text();
        let data;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (_) {
          data = null;
        }
        if (!response.ok) {
          throw new Error(getFriendlyApiError(data, text || "Unable to fetch insights."));
        }
        if (!data) throw new Error("Invalid response from server.");
        const newEntry = {
          id: data.id || Date.now(),
          text: trimmed,
          createdAt: data.created_at || new Date().toISOString(),
          insight: data,
          rating: null,
          flagged: !!data.should_pause
        };
        setPersonalEntries((prev) => [newEntry, ...prev]);
        setInsight(data);
        setEntry("");
        clearPendingQuickClarity();
        clearHistoryCacheForUser(getStoredUserId());
        loadInsights();
        setTimeout(() => {
          resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
        return true;
      } catch (err) {
        console.error(err);
        const message = err?.message || "Unable to fetch insights right now. Check your connection and try again.";
        if (message === "Subscription required") {
          setError("Please subscribe to unlock more insights.");
        } else if (message === "Daily limit reached") {
          setError("Free insight limit reached. Subscribe to continue reflecting with AI.");
        } else if (message === "Entry exceeds 500 words") {
          setError(`Please keep your entry within ${FREE_MAX_WORDS_PER_ENTRY} words.`);
        } else if (message.includes("valid entry") || message.includes("valid content")) {
          setError("Please add valid content.");
        } else {
          setError(message);
        }
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      verifiedEmail,
      isSubscriber,
      freeInsightsUsed,
      isPaywalled,
      setPersonalEntries,
      loadInsights
    ]
  );

  useEffect(() => {
    if (!verifiedEmail || resumePendingRef.current) return;
    // Wait until account insights are loaded so free-limit / paywall state is accurate.
    if (isLoadingInsights || !hasFetchedInsights) return;
    const pending = getPendingQuickClarity().trim();
    if (!pending) return;
    resumePendingRef.current = true;
    setEntry(pending);
    (async () => {
      const ok = await submitQuickClarity(pending);
      if (!ok) {
        // Keep draft visible; allow another resume attempt after the user fixes the issue.
        resumePendingRef.current = false;
        setEntry(pending);
      }
    })();
  }, [verifiedEmail, submitQuickClarity, isLoadingInsights, hasFetchedInsights]);

  const handleSubmit = async () => {
    const trimmed = entry.trim();
    const validationError = validateQuickClarityEntry(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!verifiedEmail) {
      setError("");
      setPendingQuickClarity(trimmed);
      navigate(`/login?intent=${QUICK_CLARITY_LOGIN_INTENT}`);
      return;
    }
    await submitQuickClarity(trimmed);
  };

  const updateRating = async (id, rating) => {
    if (id === null || id === undefined) return;
    if (!verifiedEmail) {
      console.error("Unable to update rating: missing email");
      return;
    }
    setPersonalEntries((prev) => prev.map((item) => (item.id === id ? { ...item, rating } : item)));
    setInsight((prev) => (prev && prev.id === id ? { ...prev, rating } : prev));
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/updateInsightRating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ insightId: id, rating, email: verifiedEmail })
      });
      if (!response.ok) {
        const text = await response.text();
        console.error("Unable to update rating", response.status, text);
      }
    } catch (err) {
      console.error("Unable to update rating", err);
    }
  };

  const handleDeleteInsight = async (id) => {
    if (id === null || id === undefined) return;
    setPersonalEntries((prev) => prev.filter((item) => item.id !== id));
    if (insight && insight.id === id) {
      setInsight(null);
    }
    try {
      await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/deleteInsight`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ insightId: id, email: verifiedEmail })
      });
    } catch (err) {
      console.error("Unable to delete insight", err);
    }
  };

  const renderInsight = (data) => {
    if (!data) return null;
    if (data.should_pause && data.risk_self_harm) {
      const crisisFallback = [
        "If you are in crisis or may hurt yourself, please reach out for human help right now.",
        "In the United States, call or text 988 (Suicide & Crisis Lifeline), 24/7, for free confidential support.",
        "If you are outside the US, call your local emergency number or search for a suicide prevention helpline in your country (for example findahelpline.com).",
        "If you or someone else is in immediate danger, contact emergency services (for example 911 in the US).",
        "Faith and prayer can matter, but trained counselors and crisis lines exist so you are not alone in this moment—please use them."
      ].join("\n\n");
      const crisisCopy = (data.support_message && String(data.support_message).trim()) || crisisFallback;
      return (
        <div className="insight-card insight-card--risk insight-card--fade" role="alert">
          <h3>Seek immediate help</h3>
          <p className="insight-card__emergency">{crisisCopy}</p>
        </div>
      );
    }
    if (data.should_pause) {
      const supportFallback = [
        "This entry includes content that needs support outside Ahavibes.",
        "Ahavibes examines interpretations; it is not crisis care, legal advice, or a reporting service.",
        "If you or someone else is in immediate danger, contact local emergency services.",
        "For confidential support, contact a qualified professional or a helpline in your country (for example findahelpline.com). In the US, call or text 988 if you are in emotional crisis."
      ].join("\n\n");
      return (
        <div className="insight-card insight-card--support insight-card--fade" role="status">
          <h3>Pause this reflection</h3>
          <p className="insight-card__emergency">
            {(data.support_message && String(data.support_message).trim()) || supportFallback}
          </p>
        </div>
      );
    }
    return (
      <div className="insight-card insight-card--fade">
        {isQuickClarityInsight(data) ? (
          <QuickClarityResult
            insight={data}
            verifiedEmail={verifiedEmail}
            onConfirmationChange={(confirmation) => {
              setInsight((prev) => (prev && prev.id === data.id ? { ...prev, confirmation } : prev));
            }}
          />
        ) : (
          <div className="insight-summary">
            <h4 className="insight-summary__title">Insight Summary</h4>
            <InsightSummary insight={data} />
          </div>
        )}
        <div className="rating-buttons">
          <button
            type="button"
            className={`rating-button ${data?.rating === "like" ? "rating-button--active" : ""}`}
            onClick={() => data?.id && updateRating(data.id, "like")}
            disabled={!data?.id}
            aria-label="Like"
          >
            <IconThumbUp className="rating-button__icon" />
          </button>
          <button
            type="button"
            className={`rating-button ${data?.rating === "dislike" ? "rating-button--active" : ""}`}
            onClick={() => data?.id && updateRating(data.id, "dislike")}
            disabled={!data?.id}
            aria-label="Dislike"
          >
            <IconThumbDown className="rating-button__icon" />
          </button>
          <button
            className="rating-button rating-button--danger"
            type="button"
            onClick={() => data?.id && handleDeleteInsight(data.id)}
            disabled={!data?.id}
            aria-label="Delete"
          >
            <IconTrash className="rating-button__icon" />
          </button>
        </div>
      </div>
    );
  };
  const submitLabel = isSubmitting ? "Examining…" : "Examine this thought";

  const composer = (
    <>
      <label className={embedded ? "brain-dump__label" : "personalized-label"} htmlFor="gratitude-entry">
        What&apos;s taking up space in your mind today?
      </label>
      {embedded ? <BrainDumpGuideCollapse /> : null}
      <ReflectionInputCard value={entry} onChange={setEntry} />
      <button
        className={`primary-button primary-button--wide${embedded ? " brain-dump__submit" : ""}`}
        onClick={handleSubmit}
        disabled={
          !entry.trim() ||
          isSubmitting ||
          (verifiedEmail && !isSubscriber && (isPaywalled || isLoadingInsights))
        }
      >
        {submitLabel}
      </button>
    </>
  );

  const content = (
    <>
      {!embedded ? (
        <div className="intro-card">
          <h2>Quick Clarity</h2>
          <p className="intro-card__seo">
            Bring what is occupying your mind. Leave seeing the interpretation more clearly—facts, assumptions, and one
            primary insight you can confirm or correct.
          </p>
          <p className="intro-card__seo">
            For a full Event → Interpretation → Evidence → Response record, use Deep Reflection from the menu.
          </p>
          <BrainDumpGuideCollapse summary="How Quick Clarity works" />
          <button type="button" className="example-toggle" onClick={() => setShowExample((prev) => !prev)}>
            {showExample ? "Hide example" : "See an example"}
          </button>
          {showExample ? (
            <div className="example-collapse">
              <p>I met an investor and presented my idea.</p>
              <p>
                They seemed genuinely interested — asked thoughtful questions, engaged deeply, and encouraged me to follow up.
                It felt promising.
              </p>
              <p>
                You know how these meetings go — there’s always a layer of professional politeness. No one says exactly what
                they’re thinking in the moment. Still, the tone seemed encouraging.
              </p>
              <p>So I followed up.</p>
              <p>And the response was: No. Just like that.</p>
              <p>
                And suddenly the mind starts racing — If it was a no, why all the interest? Why the questions? Why ask me to
                follow up? That’s where the story begins.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {embedded ? <div className="brain-dump__composer">{composer}</div> : composer}

      {!verifiedEmail ? (
        <p className="limit-note">
          <Link to={`/login?intent=${QUICK_CLARITY_LOGIN_INTENT}`}>Sign in</Link> with a verifiable email to examine
          your thought with AI and save it to Past Reflections.
        </p>
      ) : null}

      {isPaywalled ? (
        <div className="paywall-card">
          <p>Unlock unlimited insights for {PRICING_MEMBER_PRICE_SUMMARY}.</p>
          <button
            className="primary-button"
            type="button"
            onClick={async () => {
              try {
                await startCheckout(verifiedEmail);
              } catch (err) {
                console.error(err);
                setError("Unable to start checkout right now.");
              }
            }}
          >
            Subscribe
          </button>
        </div>
      ) : null}
      {!isSubscriber && !embedded ? (
        <p className="limit-note">
          Free plan: {freeInsightsUsed}/{FREE_AI_INSIGHTS_TOTAL} AI insights • Max {FREE_MAX_WORDS_PER_ENTRY} words per entry
        </p>
      ) : null}
      {error ? (
        <div className="login-error">
          <p>{error}</p>
          <button type="button" className="link-button" onClick={() => setError("")}>
            Dismiss
          </button>
        </div>
      ) : null}
      <div ref={resultsRef}>{renderInsight(insight)}</div>
      {isLoadingInsights ? <p>Loading your insights...</p> : null}
      {!embedded ? (
        <div className="history-link-row">
          <Link to="/history">View Previous Reflections</Link>
        </div>
      ) : isVerified ? (
        <div className="brain-dump__history-link">
          <Link to="/history">View Previous Reflections</Link>
        </div>
      ) : null}
    </>
  );

  if (embedded) {
    return <div className="brain-dump brain-dump--embedded">{content}</div>;
  }

  return (
    <>
      <PageContainer className="personalized-page reflection-page">{content}</PageContainer>
      {!isVerified ? (
        <div className="footer-cta">
          <h2 className="footer-cta__title">
            <svg className="footer-cta__icon" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.15" />
              <path
                d="M12 6v6l4 2"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            <Link to="/login">Get Personalized “Reflect My Day”</Link>
          </h2>
        </div>
      ) : null}
    </>
  );
};

export default BrainDump;
